import { useEffect, useRef, useState } from "react";

interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

function recognitionClass(): (new () => Recognition) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Speech to text through the browser (Chrome on Android understands Bangla). */
export function useSpeech(locale: "bn" | "en", onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const ref = useRef<Recognition | null>(null);
  const supported = typeof window !== "undefined" && recognitionClass() !== null;
  useEffect(() => () => ref.current?.stop(), []);
  const start = () => {
    const Klass = recognitionClass();
    if (!Klass) return;
    const r = new Klass();
    r.lang = locale === "bn" ? "bn-BD" : "en-US";
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      const text = Array.from(e.results)
        .map((res) => res[0]?.transcript ?? "")
        .join("");
      onText(text);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    ref.current = r;
    setListening(true);
    r.start();
  };
  const stop = () => ref.current?.stop();
  return { supported, listening, start, stop };
}
